import { AgentRun, Document, DOCUMENT_MIME_TYPES, JSONObject } from '@lucidtech/las-sdk-browser';
import { useCallback } from 'react';

import {
  toAgentRunId,
  toDocumentId,
  useCreateAgentRun,
  useCreateDocument,
  useDeleteAgentRun,
  useDeleteDocument,
  usePutFile,
} from '@/hooks/api';
import { isInArray } from '@/utils';

export type UseAgentOpts = {
  agentId?: string | null;
};

export const useAgent = ({ agentId: agentIdHookOpt }: UseAgentOpts = {}) => {
  const { mutateAsync: createAgentRun } = useCreateAgentRun();
  const { mutateAsync: createDocument } = useCreateDocument();
  const { mutateAsync: deleteAgentRun } = useDeleteAgentRun();
  const { mutateAsync: deleteDocument } = useDeleteDocument();
  const { mutateAsync: putDocumentFile } = usePutFile();

  const createRun = useCallback(
    ({
      file,
      agentId: agentIdCallbackArg,
      variables,
    }: {
      file: File;
      agentId?: string;
      variables?: JSONObject | null;
    }) => {
      return new Promise<{ run: AgentRun; document: Document; file: { fileUrl: string; blob: Blob } }>(
        (resolve, reject) => {
          const agentId = agentIdCallbackArg ?? agentIdHookOpt;

          if (!agentId) {
            const message = 'You must provide agentId';
            console.error(message);
            reject(new Error(message));
            return;
          }

          if (!isInArray(DOCUMENT_MIME_TYPES, file.type)) {
            const message = `${file.name} has unsupported mime type: ${file.type}`;
            console.error(message);
            reject(new Error(message));
            return;
          }

          createAgentRun({ agentId, variables })
            .then((run) => {
              createDocument({ name: file.name, agentRunId: run.id })
                .then((document) => {
                  putDocumentFile({ fileUrl: document.fileUrl, file })
                    .then((f) => {
                      resolve({ run, document, file: f });
                    })
                    .catch((error) => {
                      /* cleanup in reverse order */
                      deleteDocument(toDocumentId(document.documentId))
                        .finally(() => {
                          deleteAgentRun(toAgentRunId(run.agentId, run.runId)).catch(console.error);
                        })
                        .catch(console.error);

                      reject(error);
                    });
                })
                .catch((error) => {
                  /* cleanup */
                  deleteAgentRun(toAgentRunId(run.agentId, run.runId)).catch(console.error);

                  reject(error);
                });
            })
            .catch((error) => reject(error));
        }
      );
    },
    [agentIdHookOpt, createAgentRun, createDocument, putDocumentFile, deleteAgentRun, deleteDocument]
  );

  return { createRun };
};
